<script setup lang="ts">
import { KsIcon } from "@aschehoug/kloss";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

const props = defineProps<{
  variant: SingleProductVariant;
}>();

const price = usePrices(props.variant);
</script>

<template>
  <summary
    class="preview"
    :style="variant.textColor && `color: ${variant.textColor}`"
  >
    <div class="description-container">
      <div class="description">
        <h4 class="title" v-text="variant.title" />
        <p v-if="variant.languages?.length && variant.subtitle">
          {{ variant.subtitle }}&nbsp;<span class="language">&middot;&nbsp;{{ languages(variant) }}</span>
        </p>
        <p v-else-if="variant.subtitle" v-text="variant.subtitle" />
        <span
          v-else-if="variant.languages?.length"
          class="language"
          v-text="languages(variant)"
        />
      </div>
      <IconsDiscount
        v-if="price !== 'NOT_APPLICABLE' && price?.discounted"
        size="small"
        class="[--background:white] [--color:theme(colors.coral.pop)] [--stroke:none]"
      />
    </div>
    <div>
      <span class="hidden h-full uppercase tracking-[0.0625rem] xs:block" v-text="getFormats(variant)" />
      <KsIcon :icon="faChevronDown" class="transition-transform duration-200 group-open:rotate-180" />
    </div>
  </summary>
</template>

<style scoped lang="scss">
.preview {
  $layout-change: $extra-small;
  --gap: 1rem;

  display: flex;
  justify-content: space-between;
  gap: var(--gap);
  cursor: pointer;
  transition: margin 0.3s ease-out;
  align-items: center;
  padding: 1rem 0.75rem;

  @media screen and (min-width: $layout-change) {
    padding: 0;
    --gap: #{pixelsToRem(60)};
  }

  &::marker,
  &::-webkit-details-marker {
    display: none;
  }

  > * {
    display: flex;
    align-items: center;
    gap: pixelsToRem(30);
  }

  .description-container {
    gap: var(--gap);

    @media screen and (min-width: $layout-change) {
      border-right: 1px solid currentColor;
      padding-right: var(--gap);
    }
  }

  .description {
    > p,
    > span {
      font-size: 0.875rem;
      line-height: 128.571%;

      @media screen and (min-width: $layout-change) {
        line-height: 111.111%;
        font-size: 1.125rem;
      }
    }

    .title {
      font-size: 1.125rem;
      line-height: 133.333%;
      font-weight: 600;
      margin-bottom: 0.25rem;

      @media screen and (min-width: $layout-change) {
        font-size: 1.5rem;
        line-height: 125%;
      }
    }

    .language {
      font-weight: 500;
    }
  }
}
</style>
